import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { isNativePlatform } from "@/utils/nativePlugins";
import { getNativeRolePreference } from "@/utils/nativeRoleStorage";
import { Loader2 } from "lucide-react";

/**
 * Smart root redirect component that handles different flows for native vs web users.
 * - Native + Authenticated → Appropriate dashboard
 * - Native + Not Authenticated + Remembered Role → Direct to login
 * - Native + Not Authenticated → Role selector screen
 * - Web → Existing landing page flow
 */
const RootRedirect = () => {
  const { user, isLoading: authLoading, profile } = useAuth();
  const { buyerProfile, isLoading: buyerLoading } = useBuyer();
  
  const isNative = isNativePlatform();
  
  // Show loading while auth state is being determined
  if (authLoading || buyerLoading) {
    return (
      <div className="min-h-screen bg-godyspo-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-godyspo-gold" />
      </div>
    );
  }
  
  // Web users: Keep existing behavior
  if (!isNative) {
    return <Navigate to="/seller" replace />;
  }
  
  // Native + Authenticated
  if (user) {
    if (buyerProfile) {
      return <Navigate to="/buyer/dashboard" replace />;
    }
    if (profile) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/native/role-select" replace />;
  }
  
  // Native + Not Authenticated: Check remembered role
  const rememberedRole = getNativeRolePreference();
  if (rememberedRole === 'buyer') {
    return <Navigate to="/buyer/login" replace />;
  }
  if (rememberedRole === 'seller') {
    return <Navigate to="/login" replace />;
  }
  
  return <Navigate to="/native/role-select" replace />;
};

export default RootRedirect;
